import { RangeHorizon } from '@toggle/chart';
import { SmartTable } from '@toggle/design-system';
import uniqBy from 'lodash/uniqBy';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TooltipContainer } from '~/components/tooltip-container/TooltipContainer';
import {
  getInsightEpisodeDates,
  getInsightTotalTradingDays,
} from '~/services/insights/utils/insight-utils';
import { InsightPastEpisodesData } from '~/views/alfred/quick-analysis/use-chat/assistant-response.type';

import * as Shared from '../../PastEpisodesWidget.styles';
import { PastEpisodesChart } from '../past-episodes-chart/PastEpisodesChart';
import * as S from './InsightPastEpisodes.styles';
import {
  getColumns,
  getHorizonsLabels,
  mapEpisodesToTableRow,
} from './utils/utils';

export const PAST_EPISODES_HORIZONS = [
  RangeHorizon.OneMonth,
  RangeHorizon.ThreeMonths,
  RangeHorizon.SixMonths,
  RangeHorizon.OneYear,
  RangeHorizon.ThreeYears,
  RangeHorizon.Max,
];

export type EpisodeTableRow = {
  key: string;
  episodeNo: number;
  date: string;
  tradingDays: number;
};

export const InsightPastEpisodes = ({
  insight,
  entity,
}: InsightPastEpisodesData) => {
  const { t } = useTranslation(['widget', 'chart']);

  const episodes = insight.episodes;

  const totalTradingDays = getInsightTotalTradingDays(insight);

  const [activeHorizonIdx, setActiveHorizonIdx] = useState(5);
  const handleChangeHorizon = (index: number) => setActiveHorizonIdx(index);

  const columns = getColumns(totalTradingDays, t);

  const conditions = useMemo(() => {
    const { entity, snake } = insight.metadata;
    const uniqueConditions = uniqBy(insight.conditions, 'snake');

    const isInInsightsSnakes = uniqueConditions.some(
      condition =>
        condition.snake === snake ||
        (condition.snake.includes('price') && condition.snake === snake)
    );

    if (isInInsightsSnakes) {
      return insight.conditions;
    }

    return [
      {
        entity,
        snake,
      },
      ...uniqueConditions,
    ];
  }, [insight.conditions, insight.metadata]);

  const assets = conditions.map((condition, index) => ({
    tag: entity.tag,
    snake: condition.snake,
    paneIndex: index,
  }));

  return (
    <S.Root>
      <S.TitleLabel>{t('widget:pastEpisode.whenHasThisHappened')}</S.TitleLabel>
      <S.DescriptionLabel data-testid="episode-description">
        <span>
          <span>{t('widget:pastEpisode:title.occurrences')}</span>
          <Shared.PastEpisodeKey />
          <TooltipContainer
            label={t('analyze:experiment.chart.tooltip')}
            showUnderline
          >
            <span>
              {t('widget:pastEpisode:title.episodes', {
                episodes: getInsightEpisodeDates(episodes).length,
              })}
            </span>
          </TooltipContainer>
          {','}
          <span>
            {t('widget:pastEpisode:title.days', {
              totalTradingDays,
            })}
          </span>
        </span>
      </S.DescriptionLabel>
      <Shared.PastEpisodesButtons>
        <Shared.StyledButtonGroup
          labels={getHorizonsLabels(t, PAST_EPISODES_HORIZONS)}
          onClick={handleChangeHorizon}
          activeIndex={activeHorizonIdx}
          $activeIndex={activeHorizonIdx}
        />
      </Shared.PastEpisodesButtons>
      <PastEpisodesChart
        assets={assets}
        episodes={episodes}
        horizon={PAST_EPISODES_HORIZONS[activeHorizonIdx]}
        key={insight.id}
      />
      <S.TableWrapper>
        <SmartTable
          columns={columns}
          data={mapEpisodesToTableRow(episodes)}
          withBodyRowHover={false}
        />
      </S.TableWrapper>
    </S.Root>
  );
};
